/**
 * Dashboard store - holds tenant data in memory
 *
 * Persists during SPA navigation, clears on browser refresh.
 *
 * ## Why refresh() is written the way it is
 *
 * This store is the single source of truth for plan, quota and counts, and
 * several things refresh it at once: every view's onMounted, the Refresh button,
 * startTrial, and the ~10s post-checkout poll in useBilling. The previous
 * version guarded that with `if (loading) return`, which meant a concurrent
 * refresh **resolved immediately without fetching** — so `await refresh()`
 * handed the caller the *old* data while reporting success. That is how a
 * completed purchase could keep showing the previous plan: the poll held the
 * lock, and every other refresh in that window silently no-opped.
 *
 * Two rules fix it:
 *
 * 1. Concurrent callers share the in-flight promise, so everyone awaits a real
 *    fetch and sees the same result.
 * 2. A refresh requested *during* a fetch is not dropped — it sets a pending
 *    flag and runs once more afterwards. Callers ask because something changed,
 *    and the in-flight response was already on the wire before that change.
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { tenantApi } from '@/services/api'
import { getTemplateStatus } from '@/utils/templateStatus'
import { useAuthStore } from '@/stores/auth'
import { platformFeatures, type CallerAccess, type PackBalance, type Platform, type Tenant } from '@lark-automadoc/shared-types'

export const useDashboardStore = defineStore('dashboard', () => {
  // State
  const tenant = ref<Tenant | null>(null)
  const templateCount = ref(0)
  const userCount = ref(0)
  const isAppAdmin = ref(false)
  /**
   * What the signed-in person may do when they generate — their own seat
   * included. Per person, so it is not on `tenant`: two admins of one company
   * can hold different seats.
   */
  const access = ref<CallerAccess | null>(null)
  /** The company's prepaid document packs. */
  const packs = ref<PackBalance | null>(null)
  /**
   * Templates that are not ready to generate. `null` means "we could not find
   * out" — reporting 0 there claimed everything was healthy on a failed fetch.
   * Admin-only: the endpoint behind it is app_admin gated.
   */
  const templatesWithIssues = ref<number | null>(null)
  const loaded = ref(false)
  const loading = ref(false)
  /** Set when the last load failed, so views can show a retry instead of spinning. */
  const error = ref<string | null>(null)
  /** When the data currently in the store was fetched. */
  const lastLoadedAt = ref<number | null>(null)
  /**
   * The organisations the account answers for. GET /api/tenant sends it only
   * when it says something beyond Lark; absent reads as `['lark']`.
   */
  const platforms = ref<Platform[] | null>(null)
  /** The account's AutomaDoc-managed admins, for "ask your admin" (non-Lark accounts only). */
  const admins = ref<Array<{ userId: string; name: string | null }>>([])

  /**
   * How long a load stays good enough to reuse.
   *
   * Navigating between pages used to refetch every time — two API calls per
   * mount for an admin — even when you had just come from a page that loaded
   * the same data a second earlier. Nothing here changes without a user action,
   * and the actions that *do* change it (purchase, trial, delete) force a
   * refresh explicitly, so a short window is safe and removes most of the churn.
   */
  const STALE_AFTER_MS = 30_000

  // Computed
  const planStatus = computed(() => tenant.value?.planStatus || null)
  const usageStatus = computed(() => tenant.value?.usageStatus || null)

  /**
   * Which screens this session gets (shared-types platformFeatures(), design
   * §8.1) — the shell and the route guard both read it, so they cannot
   * disagree. The session's platform is the JWT's; the account's platforms
   * come with the tenant, and default to the session's own until it loads.
   */
  const features = computed(() => {
    const auth = useAuthStore()
    const session = auth.platform
    // Until the environment's platforms are known, offer to add none.
    return platformFeatures(platforms.value ?? [session], session, auth.providers?.platforms ?? ['lark'], {
      emailSending: auth.providers?.emailSending,
    })
  })

  /** Names to show a member in place of a purchase button; empty when unknown (Lark keeps its list in Lark). */
  const adminNames = computed(() => admins.value.map((a) => a.name).filter((n): n is string => !!n))

  /**
   * Set while a refresh the *user* asked for is running.
   *
   * The distinction matters because refresh() has two kinds of caller. Someone
   * who clicked Refresh is waiting on the page and needs to see that the numbers
   * are being re-fetched. The post-checkout poll calls it six times in ten
   * seconds in the background, and skeletoning that would strobe the dashboard.
   */
  const manualRefreshing = ref(false)

  /**
   * First load, with nothing on screen yet. Views replace the whole page with
   * placeholders here, header included, because there is no header content to
   * keep.
   *
   * Distinct from manualRefreshing on purpose: on a refresh the header is
   * already correct and must stay put, so only the content below is skeletoned.
   * Folding both into one flag hid the Refresh button behind the skeleton it
   * triggered, which left the spinner reachable only during *background*
   * refreshes - the exact opposite of what it is there to report.
   */
  const initialLoading = computed(() => loading.value && !loaded.value)

  function isStale(): boolean {
    return lastLoadedAt.value === null || Date.now() - lastLoadedAt.value > STALE_AFTER_MS
  }

  /**
   * Only Home renders the "N templates need setup" badge, but the fetch behind
   * it used to run on *every* dashboard load — so opening Contact or Pricing as
   * an admin cost two API calls, one of which pulled the tenant's entire
   * template list for a number nothing on the page displays. Views that need it
   * opt in.
   *
   * Sticky once set, deliberately: Home's Refresh button and the post-purchase
   * poll go through refresh(), which has no options argument, and they must keep
   * the badge current. The cost is that later loads from other pages in the same
   * session also fetch it — a fair trade for not having a stale count on the one
   * page that shows it.
   */
  let wantsIssues = false

  /** The fetch currently on the wire, shared by every concurrent caller. */
  let inFlight: Promise<void> | null = null
  /** A refresh was asked for while one was running; run once more when it settles. */
  let pending = false

  /**
   * Load dashboard data, reusing what is already in the store if it is recent.
   *
   * This is what views should call on mount. Anything that has *changed* server
   * state must call refresh() instead, which always refetches.
   */
  async function load(options: { withIssues?: boolean } = {}): Promise<void> {
    if (options.withIssues) wantsIssues = true

    // Join a fetch already on the wire rather than queueing another. The view
    // and the sidebar shell around it both load on mount, so a view asking for
    // the issue count would otherwise turn every visit to the busiest page into
    // two round trips — the opposite of the caching this
    // store exists to provide. fetchOnce reads wantsIssues *after* the tenant
    // call resolves, so the in-flight pass usually picks the flag up.
    if (inFlight) {
      await inFlight
      // Lost the race: that pass had already decided not to fetch issues.
      if (needsIssueCount(options)) await refresh()
      return
    }

    if (loaded.value && !isStale() && !needsIssueCount(options)) return
    await refresh()
  }

  /** Data is otherwise usable, but this caller needs an issue count we lack. */
  function needsIssueCount(options: { withIssues?: boolean }): boolean {
    return !!options.withIssues && isAppAdmin.value && templatesWithIssues.value === null
  }

  async function fetchOnce(): Promise<void> {
    loading.value = true
    try {
      const data = await tenantApi.getTenantInfo()
      tenant.value = data.tenant
      templateCount.value = data.templateCount
      userCount.value = data.userCount
      isAppAdmin.value = data.currentUser.is_app_admin
      access.value = data.access ?? null
      packs.value = data.packs ?? null
      platforms.value = data.platforms ?? ['lark']
      admins.value = data.admins ?? []
      error.value = null
      lastLoadedAt.value = Date.now()

      if (data.currentUser.is_app_admin && wantsIssues) {
        try {
          const { templates } = await tenantApi.getTenantTemplates()
          templatesWithIssues.value = templates.filter(t => getTemplateStatus(t) !== 'ready').length
        } catch {
          // Non-critical, don't block the dashboard — but don't claim zero either.
          templatesWithIssues.value = null
        }
      } else if (!data.currentUser.is_app_admin) {
        templatesWithIssues.value = null
      }
    } catch (err) {
      console.error('[Dashboard] Failed to load data:', err)
      // Surfaced rather than swallowed: without this a first-load failure left
      // every view on its loading spinner forever, with no error and no retry.
      error.value = err instanceof Error ? err.message : 'Failed to load dashboard'
    } finally {
      // Set even on failure: "we tried" is what lets a view stop spinning and
      // render the error state.
      loaded.value = true
      loading.value = false
    }
  }

  /**
   * Force refresh dashboard data.
   *
   * Safe to call concurrently — see the note at the top of this file.
   *
   * Pass `manual: true` from a Refresh button so the view shows placeholders
   * instead of the numbers it is about to replace. It is deliberately opt-in:
   * the default is a background refresh, because most callers here are reacting
   * to something rather than being waited on.
   */
  function refresh(options: { manual?: boolean } = {}): Promise<void> {
    // Set even when joining an existing fetch: the user clicked, so they should
    // see the state regardless of who started the request they end up riding.
    if (options.manual) manualRefreshing.value = true

    if (inFlight) {
      pending = true
      return inFlight
    }

    inFlight = (async () => {
      try {
        await fetchOnce()
        // Someone asked again mid-flight; their reason post-dates this response.
        while (pending) {
          pending = false
          await fetchOnce()
        }
      } finally {
        inFlight = null
        pending = false
        manualRefreshing.value = false
      }
    })()

    return inFlight
  }

  return {
    // State
    tenant,
    templateCount,
    userCount,
    isAppAdmin,
    access,
    packs,
    templatesWithIssues,
    loaded,
    loading,
    initialLoading,
    manualRefreshing,
    error,
    lastLoadedAt,
    platforms,
    admins,

    // Computed
    planStatus,
    usageStatus,
    features,
    adminNames,

    // Actions
    load,
    refresh
  }
})
